import { queueTrackingEvent } from '@library/tracking-integration'

// Snowplow tracking for the Gousto Anytime delivery-settings dropdown (TM-1487).

export type OrderDetailsTrackingProperties = {
  delivery_date: string
  num_portions: number
  postcode: string
  transaction_type: 'transactional'
}

type ClickOrderDetailsDropdownEvent = {
  action: 'click_order_details_dropdown'
  category: 'funnel'
  properties: OrderDetailsTrackingProperties
}

// banner_state reflects what was actually on screen when eligibility settled — 'collapsed'
// if the customer already dismissed the panel by then.
export type OrderDetailsDropdownImpressionProperties = OrderDetailsTrackingProperties & {
  banner_state: 'expanded' | 'collapsed'
  discount_shown: boolean
  discount_offered_pct: number | null
  discount_offered_fixed: number | null
}

type ViewOrderDetailsDropdownEvent = {
  action: 'view_order_details_dropdown'
  category: 'funnel'
  properties: OrderDetailsDropdownImpressionProperties
}

export type OrderDetailsDropdownCloseMethod = 'toggle' | 'outside_click' | 'scroll'

export type CloseOrderDetailsDropdownProperties = OrderDetailsTrackingProperties & {
  close_method: OrderDetailsDropdownCloseMethod
}

type CloseOrderDetailsDropdownEvent = {
  action: 'close_order_details_dropdown'
  category: 'funnel'
  properties: CloseOrderDetailsDropdownProperties
}

type EditOrderDetailsAddressEvent = { action: 'edit_order_details_address' }
type EditOrderDetailsBoxSizeEvent = { action: 'edit_order_details_box_size' }
type EditOrderDetailsDeliveryDayEvent = { action: 'edit_order_details_delivery_day' }

type EditOrderDetailsAddressCompletedEvent = {
  action: 'edit_order_details_address_completed'
  properties: { previous_postcode: string; updated_postcode: string }
}
type EditOrderDetailsBoxSizeCompletedEvent = {
  action: 'edit_order_details_box_size_completed'
  properties: { previous_num_portions: number; updated_num_portions: number }
}
type EditOrderDetailsDeliveryDayCompletedEvent = {
  action: 'edit_order_details_delivery_day_completed'
  properties: { previous_delivery_date: string; updated_delivery_date: string }
}

export const trackOrderDetailsDropdownOpened = (properties: OrderDetailsTrackingProperties) =>
  queueTrackingEvent<ClickOrderDetailsDropdownEvent>({
    action: 'click_order_details_dropdown',
    category: 'funnel',
    properties,
  })

export const trackOrderDetailsDropdownImpression = (
  properties: OrderDetailsDropdownImpressionProperties,
) =>
  queueTrackingEvent<ViewOrderDetailsDropdownEvent>({
    action: 'view_order_details_dropdown',
    category: 'funnel',
    properties,
  })

export const trackOrderDetailsDropdownClosed = (properties: CloseOrderDetailsDropdownProperties) =>
  queueTrackingEvent<CloseOrderDetailsDropdownEvent>({
    action: 'close_order_details_dropdown',
    category: 'funnel',
    properties,
  })

export const trackEditOrderDetailsAddress = () =>
  queueTrackingEvent<EditOrderDetailsAddressEvent>({ action: 'edit_order_details_address' })

export const trackEditOrderDetailsBoxSize = () =>
  queueTrackingEvent<EditOrderDetailsBoxSizeEvent>({ action: 'edit_order_details_box_size' })

export const trackEditOrderDetailsDeliveryDay = () =>
  queueTrackingEvent<EditOrderDetailsDeliveryDayEvent>({
    action: 'edit_order_details_delivery_day',
  })

export const trackEditOrderDetailsAddressCompleted = (properties: {
  previous_postcode: string
  updated_postcode: string
}) =>
  queueTrackingEvent<EditOrderDetailsAddressCompletedEvent>({
    action: 'edit_order_details_address_completed',
    properties,
  })

export const trackEditOrderDetailsBoxSizeCompleted = (properties: {
  previous_num_portions: number
  updated_num_portions: number
}) =>
  queueTrackingEvent<EditOrderDetailsBoxSizeCompletedEvent>({
    action: 'edit_order_details_box_size_completed',
    properties,
  })

export const trackEditOrderDetailsDeliveryDayCompleted = (properties: {
  previous_delivery_date: string
  updated_delivery_date: string
}) =>
  queueTrackingEvent<EditOrderDetailsDeliveryDayCompletedEvent>({
    action: 'edit_order_details_delivery_day_completed',
    properties,
  })
